import { BottomNavigation } from '@/components/Layout/BottomNavigation'
import { Header } from '@/components/Layout/Header'
import { RaundLifeCycleDebug } from '@/components/Layout/debug/RaundLifeCycleDebug'
import { GeofenceMessage } from '@/components/Models/geofence/GeofenceMessage'
import { CampaignComponents } from '@/components/Pages/home/components/CampaignComponents'
import { DialogCampaignDescription } from '@/components/Pages/home/components/DialogCampaignDescription'
import { DialogCampaignHowToPlay } from '@/components/Pages/home/components/DialogCampaignHowToPlay'
import { DialogFirstLoginConfirm } from '@/components/Pages/home/components/DialogFirstLoginConfirm'
import { DialogPointHistory, type PointHistoryEntry } from '@/components/Pages/home/components/DialogPointHistory'
import { DialogStampDetail } from '@/components/Pages/home/components/DialogStampDetail'
import { PointCardSheet } from '@/components/Pages/home/components/PointCardSheet'
import { PointRallyCourseCard } from '@/components/Pages/home/components/PointRallyCourseCard'
import { StampSheet } from '@/components/Pages/home/components/StampSheet'
import { StampSheetSkeleton } from '@/components/Pages/home/components/StampSheetSkeleton'
import { RewardList } from '@/components/Pages/rewards/components/list/RewardList'
import { RewardListSkeleton } from '@/components/Pages/rewards/components/list/RewardListSkeleton'
import { Button, Image, Skeleton } from '@/components/UI'
import { Dialog } from '@/components/UI/Dialog'
import { Tabs } from '@/components/UI/Tabs'
import { Typography } from '@/components/UI/Typography'
import { useApi } from '@/core/api/useApi'
import { useCampaignSlug } from '@/core/app/useCampaignSlug'
import { AppMachineContext } from '@/core/app/useRaundLifeCycle'
import { useNotificationBadge } from '@/domain/popup/useNotificationBadge'
import { buildDisplayRewards } from '@/domain/reward/rewardGrouping'
import { formatAcquiredAt } from '@/lib/date/datetime'
import { useSwiperGestureRecovery } from '@/lib/dom/useSwiperGestureRecovery'
import type { SheetData, SpotInStampData, StampInSheetData, UserSheetData, UserSheetHistoryData } from '@/schemas/v2'
import type { CampaignSheetMode } from '@/schemas/v2/campaign'
import type { CampaignData } from '@/schemas/v2/campaign'
import type { PopupItemData } from '@/schemas/v2/popup'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useHomeStateMachine } from './useHomeStateMachine'

const isUserSheetData = (sheet: UserSheetData | SheetData): sheet is UserSheetData =>
  'history' in sheet && Array.isArray((sheet as UserSheetData).history)

/**
 * スタンプ獲得履歴をポイント履歴ダイアログ用アイテムに変換する
 *
 * @param canNavigate false のときは spotId を null にしてスタンプ名をタップ不可にする（canNavigateToSpot・#2972）
 */
function buildPointHistoryItems(
  history: UserSheetHistoryData[],
  stamps: StampInSheetData[],
  canNavigate: boolean,
): PointHistoryEntry[] {
  // history.point が null/undefined の場合（旧データ）はマスタ値にフォールバック
  const stampPointMap = new Map(stamps.map((s) => [s.id, s.point]))
  return history.map((h) => ({
    id: h.id,
    label: h.name,
    point: h.point ?? stampPointMap.get(h.stampId) ?? 0,
    acquiredAt: formatAcquiredAt(h.createdAt),
    // 遷移先はバックエンドが解決済み（獲得スポットが非表示なら null）
    spotId: canNavigate ? (h.spotId ?? null) : null,
  }))
}

/** ポイントカードの獲得履歴をダイアログ用アイテムに変換する（新しい順・pt は表示しない・#2970） */
function buildStampHistoryItems(history: UserSheetHistoryData[]): PointHistoryEntry[] {
  // API の history は createdAt DESC（新しい順）なのでそのまま使う
  return history.map((h) => ({
    id: h.id, // user_point_transactions.id なので一意
    label: h.name,
    acquiredAt: formatAcquiredAt(h.createdAt),
    // point は渡さない（pt 列を非表示にする）
    // ポイントカードの獲得履歴からのスポット詳細遷移は #2972 の対象外（スタンプ押下で DialogStampDetail から遷移できる）
    spotId: null,
  }))
}

/** mappedSheets の各スタンプの型（マスタデータ＋ユーザー取得状況） */
type StampWithAcquisition = {
  id: number
  name: string
  spotId: number
  spots: SpotInStampData[]
  period: boolean
  point: number
  image: string
  overlayImage: string | null
  isHas: boolean
  hasCount: number
  stampCount: number
  earnedPoints: number
}

/** mappedSheets の各シートの型（マスタデータ＋ユーザー獲得状況） */
type SheetWithAcquisition = {
  id: number
  name: string
  type: 'stamp_rally' | 'point_rally' | 'point_card' | 'overlay' | 'none'
  description: string
  earnedPoints: number
  startAt: string
  endAt: string
  total: number
  hasCount: number
  backgroundImage: string
  isLocked: boolean
  /** 購入連動アクセス制御によるロック（#3281 COURSE_UNLOCK） */
  locked: boolean
  /**
   * 「スタンプなし画像」URL（#2970 ポイントカードの未獲得枠用）。
   * sheets API の stamps[].image はキャンペーン設定 → システムデフォルトの順で解決済みの未獲得画像
   */
  nonStampImage: string | null
  stamps: StampWithAcquisition[]
  history: UserSheetHistoryData[]
}

/**
 * シートからスポット詳細へ遷移してよいか（スタンプ詳細ダイアログ・ポイント履歴で共通のガード）。
 * 開放日時前（isLocked）と購入未開放（locked）のシートは遷移させない。
 */
const canNavigateToSpot = (sheet: Pick<SheetWithAcquisition, 'isLocked' | 'locked'>): boolean =>
  !sheet.isLocked && !sheet.locked

/** シートの種別・backgroundImage から StampSheet に渡す campaign オブジェクトを生成する */
function buildSheetCampaignMeta(
  sheet: SheetWithAcquisition,
  campaignIsRepeatStamp: boolean | null,
  campaignStampShape: number | null,
): {
  isRepeatStamp: boolean
  stampShape: 0 | 1
  sheetMode: CampaignSheetMode
} {
  return {
    isRepeatStamp: campaignIsRepeatStamp ?? false,
    stampShape: campaignStampShape === 1 ? 1 : 0,
    sheetMode:
      sheet.type === 'overlay'
        ? 'OVERLAY'
        : sheet.type === 'stamp_rally' && sheet.backgroundImage
          ? 'BACKGROUND'
          : 'NORMAL',
  }
}

export function HomePageSkeleton() {
  const { t } = useTranslation()

  return (
    // h-dvh + main の min-h-0 でスクロールコンテナを main に閉じ込める。
    // ドキュメント全体がスクロールすると、スクロールバーがヘッダー領域まで伸びてしまう。
    <div className="flex h-dvh flex-col">
      <Header title={t('common.home', 'ホーム')} showLanguage onNotification={() => {}} />
      <main className="min-h-0 flex-1 overflow-y-auto pb-[72px]">
        <CampaignComponents
          image={''}
          period={true}
          isDisplayEventPeriod={false}
          viewStartAt={undefined}
          viewEndAt={undefined}
          showCampaignDescription={true}
          isLoading={true}
          onClickSummary={() => {}}
          onClickHowToPlay={() => {}}
        />
        <div className="mt-4 px-4">
          <div className="flex gap-4 border-b border-gray-200 pb-2">
            <Skeleton variant="text" width={90} height={20} />
            <Skeleton variant="text" width={90} height={20} />
          </div>
          {/* 読み込み完了後の StampSheet と同じ上余白（pt-4）にする */}
          <div className="pt-4">
            <StampSheetSkeleton />
          </div>
        </div>
        {process.env.NODE_ENV !== 'production' && <RaundLifeCycleDebug />}
      </main>
      <BottomNavigation activeItem="home" />
    </div>
  )
}

type HomePageInnerProps = {
  isAuthenticated: boolean
  campaignData: CampaignData | null
  isShowingConfirm: boolean
  homePopupItems: PopupItemData[]
  onConfirmClose: (skipNext?: boolean) => void
}

export function HomePageInner({
  isAuthenticated,
  campaignData,
  isShowingConfirm,
  homePopupItems,
  onConfirmClose,
}: HomePageInnerProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const { gen, loginUrl } = useCampaignSlug()
  const api = useApi()
  const [activeTab, setActiveTab] = useState(0)

  // 購入後 /?session_id=... で戻った際に表示する購入完了ダイアログ（URL から派生し、閉じたら dismissed で抑止）
  const [purchaseCompleteDismissed, setPurchaseCompleteDismissed] = useState(false)

  const purchaseSessionId =
    router.isReady && typeof router.query.session_id === 'string' ? router.query.session_id : null
  const showPurchaseComplete = purchaseSessionId !== null && !purchaseCompleteDismissed

  // session_id から決済詳細を取得し、購入した商品名をダイアログに表示する
  const { data: purchaseDetail, isLoading: purchaseDetailLoading } = useQuery({
    queryKey: ['checkoutSessionDetail', purchaseSessionId],
    queryFn: () => api.auth.fetchCheckoutSessionDetail(purchaseSessionId as string),
    enabled: isAuthenticated && showPurchaseComplete,
  })
  const purchasedProductName = purchaseDetail?.items.map((item) => item.productName).join('、') ?? ''

  // 購入完了ダイアログを閉じる: dismissed にして URL から session_id を除去する（リロードで再表示しない）
  const handleClosePurchaseComplete = () => {
    setPurchaseCompleteDismissed(true)
    const rest = Object.fromEntries(Object.entries(router.query).filter(([key]) => key !== 'session_id'))
    router.replace({ pathname: router.pathname, query: rest }, undefined, { shallow: true })
  }

  const { hasUnread } = useNotificationBadge(isAuthenticated)

  // 重ね絵(OVERLAY)画像は非同期ロードのため、autoHeight の初期測定では高さを取りこぼし
  // 下部がクリップされる。画像ロード後に updateAutoHeight() でスライド高さを測り直す。
  // ref は pointercancel 復帰フックと共用する（Chrome で Swiper が操作不能になる対策）
  const stampSwiperRef = useSwiperGestureRecovery()
  const pointRallySwiperRef = useSwiperGestureRecovery()

  const {
    state,
    isLoading,
    isError,
    isSummaryOpen,
    isHowToPlayOpen,
    isStampDetailOpen,
    isPointHistoryOpen,
    selectedStamp,
    sheetsData,
    rewardsData,
    pointHistoryItems,
    pointHistoryVariant,
    send: pageSend,
  } = useHomeStateMachine({
    input: {
      isAuthenticated,
    },
  })

  const isTokenRefreshWaiting = AppMachineContext.useSelector((s) => s.matches({ phase3: { tokenRefresh: 'waiting' } }))
  const prevRef = useRef({
    isTokenRefreshWaiting,
    isWaitingForRefresh: state.matches('waitingForRefresh'),
  })
  useEffect(() => {
    const prev = prevRef.current
    const isWaitingForRefresh = state.matches('waitingForRefresh')
    const current = { isTokenRefreshWaiting, isWaitingForRefresh }
    prevRef.current = current

    const tokenJustEnteredWaiting = current.isTokenRefreshWaiting && !prev.isTokenRefreshWaiting
    const stateJustEnteredWaiting = current.isWaitingForRefresh && !prev.isWaitingForRefresh

    if (
      current.isWaitingForRefresh &&
      current.isTokenRefreshWaiting &&
      (tokenJustEnteredWaiting || stateJustEnteredWaiting)
    ) {
      pageSend({ type: 'REFRESH_COMPLETE' })
    }
  }, [isTokenRefreshWaiting, state, pageSend])

  const mappedSheets: SheetWithAcquisition[] = (sheetsData?.sheets ?? []).map((sheet) => {
    const userSheet = isUserSheetData(sheet) ? sheet : null
    const history = userSheet?.history ?? []
    const historyMap = new Map(history.map((h) => [h.stampId, h]))

    const sheetHasCount =
      sheet.type === 'stamp_rally'
        ? (userSheet?.meta?.acquiredStampCount ?? 0)
        : sheet.type === 'point_card'
          ? (userSheet?.meta?.totalStampCount ?? 0)
          : 0

    const earnedPoints = sheet.type === 'point_rally' ? (userSheet?.meta?.totalPoint ?? 0) : 0

    return {
      id: sheet.id,
      name: sheet.name,
      type: sheet.type,
      description: '',
      earnedPoints,
      startAt: sheet.startAt ?? '',
      endAt: '',
      total: sheet.totalCount,
      hasCount: sheetHasCount,
      backgroundImage: sheet.backgroundImage ?? '',
      isLocked: sheet.isLocked ?? false,
      locked: sheet.locked ?? false,
      // マスタ stamps の image は未獲得画像で全スタンプ同値のため先頭から取る（history 上書き前の値）
      nonStampImage: sheet.stamps[0]?.image ?? null,
      stamps: sheet.stamps.map((stamp) => {
        const historyEntry = historyMap.get(stamp.id)
        return {
          id: stamp.id,
          name: stamp.name,
          spotId: stamp.spots?.[0]?.id ?? 0,
          spots: stamp.spots ?? [],
          period: true,
          point: stamp.point,
          image: historyEntry?.image ?? stamp.image ?? '',
          overlayImage: historyEntry?.overlayImage ?? null,
          isHas: (historyEntry?.acquiredCount ?? 0) >= 1,
          hasCount: historyEntry?.acquiredCount ?? 0,
          stampCount: historyEntry?.acquiredCount ?? 0,
          earnedPoints: 0,
        }
      }),
      history,
    }
  })

  const handleCheckin = (spotId: number) => {
    router.push({ pathname: gen('/spots/detail'), query: { id: spotId } })
  }

  /**
   * ロック済みシート押下: 商品一覧へ遷移する。
   *
   * 購入導線は商品一覧に一本化し、ホームからは Checkout を呼ばない。
   * - `sheet.unlock` は見ない。#3521 以降 `locked=true` かつ `unlock=null`（コース開放商品が
   *   販売期間外／チケット購入でのみ開放されるシート）が正当な状態になったため、
   *   `unlock` の有無を条件にすると押しても無反応なシートが生まれる
   * - 未ログインでも遷移させる。商品一覧は未認証でも「前提購入なし商品」を返す契約で、
   *   ログイン誘導は商品一覧の「購入する」押下時に出る
   */
  const handleUnlockTap = () => {
    void router.push(gen('/products/tickets'))
  }

  // スタンプラリータブに表示するシート（stamp_rally / point_card / overlay）
  const stampRallySheets = mappedSheets.filter(
    (s) => s.type === 'stamp_rally' || s.type === 'point_card' || s.type === 'overlay',
  )

  // PointCardSheet の useEffect 依存に入るため useCallback で安定化する（inline 関数だと毎レンダー発火する）
  const handleSheetLayoutChange = useCallback(() => stampSwiperRef.current?.updateAutoHeight(), [stampSwiperRef])

  /** シート 1 枚の描画（Swiper 内・単一シート時で共通利用）。point_card は専用の台紙 UI に振り分ける（#2970） */
  const renderSheet = (sheet: SheetWithAcquisition) => {
    // ロック中（開放前 / 購入連動）は種別を問わず既存の StampSheet のロック表示に委ねる
    if (sheet.type === 'point_card' && canNavigateToSpot(sheet)) {
      return (
        <PointCardSheet
          sheetName={sheet.name}
          history={sheet.history}
          stamps={sheet.stamps}
          acquiredCount={sheet.hasCount}
          stampShape={campaignData?.stampShape === 1 ? 'square' : 'circular'}
          backgroundImage={sheet.backgroundImage || null}
          nonStampImage={sheet.nonStampImage}
          onStampClick={(stamp) => pageSend({ type: 'OPEN_STAMP_DETAIL', stamp })}
          onClickHistory={() =>
            pageSend({
              type: 'OPEN_POINT_HISTORY',
              items: buildStampHistoryItems(sheet.history),
              variant: 'stamp',
            })
          }
          onLayoutChange={handleSheetLayoutChange}
        />
      )
    }
    return (
      <StampSheet
        sheet={sheet}
        campaign={buildSheetCampaignMeta(sheet, campaignData?.isRepeatStamp ?? null, campaignData?.stampShape ?? null)}
        isLocked={sheet.isLocked}
        purchaseLocked={sheet.locked}
        onPurchaseUnlock={handleUnlockTap}
        onStampClick={(stamp) => {
          if (canNavigateToSpot(sheet)) pageSend({ type: 'OPEN_STAMP_DETAIL', stamp })
        }}
        onOverlayImageLoad={() => stampSwiperRef.current?.updateAutoHeight()}
      />
    )
  }

  // ポイントラリータブに表示するシート（point_rally のみ）
  const pointRallySheets = mappedSheets.filter((s) => s.type === 'point_rally')

  // point_rally シートが 0 件の場合はタブ 0 に強制（派生値）
  const effectiveActiveTab = pointRallySheets.length === 0 ? 0 : activeTab

  // 両タイプが存在する場合のみタブを表示する
  const showTabs = stampRallySheets.length > 0 && pointRallySheets.length > 0

  const displayRewards = useMemo(() => buildDisplayRewards(t, rewardsData?.rewards ?? []), [rewardsData, t])

  const campaignBgImage = campaignData?.backgroundImage ?? null
  const mainBackgroundStyle: CSSProperties = campaignBgImage
    ? {
        backgroundImage: `url(${campaignBgImage})`,
        backgroundRepeat: 'repeat-y',
        backgroundSize: '100% auto',
        // main 自体がスクロールコンテナになったため、既定の `scroll` では背景が
        // ビューポート1画面分に固定されてしまう。`local` でコンテンツと一緒にスクロールさせ、
        // ドキュメントスクロール時と同じ見た目（コンテンツ全長に repeat-y）を保つ。
        backgroundAttachment: 'local',
      }
    : {}

  return (
    // h-dvh + main の min-h-0 でスクロールコンテナを main に閉じ込める。
    // ドキュメント全体がスクロールすると、スクロールバーがヘッダー領域まで伸びてしまう。
    <div className="flex h-dvh flex-col">
      <Header
        title={t('common.home', 'ホーム')}
        showLanguage
        onNotification={() => router.push(gen('/news'))}
        hasUnreadBadge={hasUnread}
      />
      <main className="min-h-0 flex-1 overflow-y-auto pb-[72px]" style={mainBackgroundStyle}>
        <CampaignComponents
          image={campaignData?.image ?? ''}
          viewStartAt={campaignData?.viewStartAt}
          viewEndAt={campaignData?.viewEndAt}
          period={true}
          isDisplayEventPeriod={campaignData?.isDisplayEventPeriod ?? false}
          showCampaignDescription={true}
          isLoading={isLoading}
          onClickSummary={() => pageSend({ type: 'OPEN_SUMMARY' })}
          onClickHowToPlay={() => pageSend({ type: 'OPEN_HOW_TO_PLAY' })}
        />
        {campaignData?.homeExpansionTopImage && (
          <div className="mt-4" data-testid="home-expansion-top-image">
            <Image src={campaignData.homeExpansionTopImage} fit="width-fit" />
          </div>
        )}
        {!isAuthenticated && (
          <div className="flex justify-center mt-4 px-4">
            <Button
              label={t('home.join', '参加する')}
              fullWidth
              onClick={() => {
                // loginUrl には return_to と lang が付いている（#3504）。
                // ここで URL を組み立て直すと、Auth へ渡すパラメータの追加漏れが起きる
                location.href = loginUrl
              }}
            />
          </div>
        )}
        <div className="mt-4">
          {isLoading ? (
            <div className="px-4">
              <div className="flex gap-4 border-b border-gray-200 pb-2">
                <Skeleton variant="text" width={90} height={20} />
                <Skeleton variant="text" width={90} height={20} />
              </div>
              {/* 読み込み完了後の StampSheet と同じ上余白（pt-4）にする */}
              <div className="pt-4">
                <StampSheetSkeleton />
              </div>
            </div>
          ) : isError ? (
            <div className="px-4 py-8 flex flex-col items-center gap-4 text-center">
              <p className="text-sm text-gray-600">{t('common.fetch_failed', 'データの取得に失敗しました')}</p>
              <button
                type="button"
                className="px-4 py-2 bg-main text-on-main rounded-full text-sm"
                onClick={() => pageSend({ type: 'RETRY' })}
              >
                {t('common.retry', '再試行')}
              </button>
            </div>
          ) : (
            <>
              {showTabs && (
                <Tabs
                  tabs={[t('home.tab.stamp_rally', 'スタンプラリー'), t('home.tab.point_rally', 'ポイントラリー')]}
                  activeIndex={effectiveActiveTab}
                  onChange={setActiveTab}
                />
              )}
              {(showTabs ? effectiveActiveTab === 0 : stampRallySheets.length > 0) && (
                <div className="px-4">
                  {stampRallySheets.length > 1 ? (
                    <>
                      <div id="stamp-sheet-pagination" className="flex justify-center pb-2 pt-4" />
                      <Swiper
                        modules={[Pagination]}
                        pagination={{ clickable: true, el: '#stamp-sheet-pagination' }}
                        spaceBetween={16}
                        slidesPerView={1}
                        autoHeight
                        // ダイアログ（Mantine Modal）開閉でスライド遷移が中断され swiper.animating が
                        // true のまま stuck すると、preventInteractionOnTransition(デフォルトtrue)により
                        // ドラッグ・ドット操作が全てブロックされるため false にする（#3281 スワイプ不能対策）
                        preventInteractionOnTransition={false}
                        // Swiper は「フォーカス中の focusable 要素上で始まったドラッグ」を無視する。
                        // ダイアログを閉じると Mantine の returnFocus がロックオーバーレイ（シート全面の
                        // button）へフォーカスを戻すため、直後のスワイプが全く効かなくなる。
                        // デフォルト値から button を除外し、ボタン上でもドラッグを許可する
                        focusableElements="input, select, option, textarea, video, label"
                        onSwiper={(swiper) => {
                          stampSwiperRef.current = swiper
                        }}
                      >
                        {stampRallySheets.map((sheet) => (
                          <SwiperSlide key={sheet.id}>{renderSheet(sheet)}</SwiperSlide>
                        ))}
                      </Swiper>
                    </>
                  ) : stampRallySheets.length === 1 ? (
                    <div className="pt-4">{renderSheet(stampRallySheets[0])}</div>
                  ) : null}
                </div>
              )}
              {(showTabs ? effectiveActiveTab === 1 : pointRallySheets.length > 0) && (
                <div className="px-4">
                  {pointRallySheets.length > 1 ? (
                    <>
                      <div id="point-rally-pagination" className="flex justify-center pb-2 pt-4" />
                      <Swiper
                        modules={[Pagination]}
                        pagination={{ clickable: true, el: '#point-rally-pagination' }}
                        spaceBetween={16}
                        slidesPerView={1}
                        // swiper.animating が stuck しても操作をブロックしない（#3281 スワイプ不能対策）
                        preventInteractionOnTransition={false}
                        // フォーカス中の button 上でもドラッグを許可する（ロックオーバーレイ対策・stamp 側と同様）
                        focusableElements="input, select, option, textarea, video, label"
                        onSwiper={(swiper) => {
                          pointRallySwiperRef.current = swiper
                        }}
                      >
                        {pointRallySheets.map((sheet) => (
                          <SwiperSlide key={sheet.id}>
                            <PointRallyCourseCard
                              courseName={sheet.name}
                              earnedPoints={sheet.earnedPoints}
                              locked={sheet.locked}
                              onPurchaseUnlock={handleUnlockTap}
                              onClickHistory={() =>
                                pageSend({
                                  type: 'OPEN_POINT_HISTORY',
                                  items: buildPointHistoryItems(sheet.history, sheet.stamps, canNavigateToSpot(sheet)),
                                })
                              }
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </>
                  ) : (
                    <div className="pt-4">
                      <PointRallyCourseCard
                        courseName={pointRallySheets[0].name}
                        earnedPoints={pointRallySheets[0].earnedPoints}
                        locked={pointRallySheets[0].locked}
                        onPurchaseUnlock={handleUnlockTap}
                        onClickHistory={() =>
                          pageSend({
                            type: 'OPEN_POINT_HISTORY',
                            items: buildPointHistoryItems(
                              pointRallySheets[0].history,
                              pointRallySheets[0].stamps,
                              canNavigateToSpot(pointRallySheets[0]),
                            ),
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {campaignData?.homeExpansionBottomImage && (
            <div className="mt-4">
              <img
                src={campaignData.homeExpansionBottomImage}
                alt=""
                className="w-full"
                data-testid="home-expansion-bottom-image"
              />
            </div>
          )}
        </div>
        {/* 特典一覧 */}
        {(rewardsData?.rewards?.length ?? 0) > 0 && (
          <div className="mt-6">
            <Typography variant="h3" className="px-4 mb-2">
              {t('bottom_navigation.reward', '特典')}
            </Typography>
            {isLoading ? <RewardListSkeleton /> : <RewardList rewards={displayRewards} />}
          </div>
        )}
        {/* 以下のダイアログはcampaignDataが読み込まれた後で表示されるので、||を使って問題ない */}
        <DialogCampaignDescription
          description={campaignData?.description || ''}
          isOpen={isSummaryOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
        />
        <DialogCampaignHowToPlay
          usage={campaignData?.usageHtml || ''}
          isOpen={isHowToPlayOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
        />
        <DialogStampDetail
          stamp={selectedStamp}
          isOpen={isStampDetailOpen}
          onClose={() => pageSend({ type: 'CLOSE' })}
          onCheckin={handleCheckin}
          stampShape={campaignData?.stampShape === 1 ? 1 : 0}
        />
        <DialogFirstLoginConfirm
          images={homePopupItems.map((p) => p.image).filter((img): img is string => img !== null)}
          campaignName={campaignData?.name || ''}
          isOpen={isShowingConfirm}
          onClose={() => onConfirmClose()}
          onConfirm={(skipNext) => onConfirmClose(skipNext)}
        />
        <DialogPointHistory
          items={pointHistoryItems}
          isOpen={isPointHistoryOpen}
          // ポイントカードの獲得履歴（#2970）はタイトルを差し替える。undefined なら既存の「ポイント履歴」
          title={pointHistoryVariant === 'stamp' ? t('home.point_card.history_title', '獲得履歴') : undefined}
          onClose={() => pageSend({ type: 'CLOSE' })}
          onSelectSpot={handleCheckin}
        />
        {/* 購入完了ダイアログ（/?session_id=... で戻った際に表示） */}
        <Dialog
          selectorState={showPurchaseComplete}
          title={t('ec.payment_complete.title', '決済完了')}
          closeAction={handleClosePurchaseComplete}
        >
          <div className="flex flex-col gap-4 pt-2">
            <div className="text-center">
              <p className="text-[14px] text-gray-600">
                {t('ec.payment_complete.heading', 'ご購入ありがとうございます')}
              </p>
              <p className="text-[14px] text-gray-600">
                {t('ec.payment_complete.description', '決済が完了しました。購入内容は履歴からご確認いただけます。')}
              </p>
              {purchaseDetailLoading ? (
                <div className="mt-2 flex justify-center">
                  <Skeleton variant="text" width="60%" height={16} />
                </div>
              ) : (
                purchasedProductName && <p className="mt-2 text-[14px] font-bold text-black">{purchasedProductName}</p>
              )}
            </div>
            <Button
              label={t('common.close', '閉じる')}
              variant="primary"
              size="md"
              fullWidth
              onClick={handleClosePurchaseComplete}
            />
          </div>
        </Dialog>
        {process.env.NODE_ENV !== 'production' && <RaundLifeCycleDebug />}
      </main>

      {/* 入退室メッセージ（Geofence / #3259）: GPS 監視で入室・退出を検知し Snackbar 表示 */}
      <GeofenceMessage />

      <BottomNavigation activeItem="home" />
    </div>
  )
}
